import React, { useState, useEffect, useMemo } from "react";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { CATEGORY_OPTIONS, matchesCategory } from "../constants/categories";
import { getContentTypeLabel } from "../constants/contentTypes";


const CategoryList = () => {
  const [videoList, setVideoList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Category");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const navigate = useNavigate(); // ✅ Initialize navigation

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}/videos/with-title`);
      const data = await res.json();

      if (data.success) {
        setVideoList(data.data.videos || []);
      } else {
        alert("Failed to fetch videos");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching videos");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLock = async (video) => {
    try {
      const updatedLock = !video.lock;

      await fetch(`${config.BASE_URL}/toggle-lock/${video.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lock: updatedLock }),
      });

      setVideoList((prev) =>
        prev.map((v) => (v.id === video.id ? { ...v, lock: updatedLock } : v))
      );
    } catch (err) {
      console.error(err);
      alert("Error updating video lock status");
    }
  };

  const confirmDelete = async () => {
    try {
      const res = await fetch(
        `${config.BASE_URL}/videos/${deleteDialog.id}`,
        { method: "DELETE" }
      );
      const data = await res.json();

      if (data.success) {
        setVideoList((prev) =>
          prev.filter((v) => v.id !== deleteDialog.id)
        );
        setDeleteDialog({ open: false, id: null });
      } else {
        alert("Failed to delete video");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting video");
    }
  };

  const categoryOptions = ["All Category", ...CATEGORY_OPTIONS];

  const filteredVideos = useMemo(() => {
    return videoList.filter((v) => matchesCategory(v.category, selectedCategory));
  }, [selectedCategory, videoList]);

  // ✅ Sorting logic
  const sortedVideos = useMemo(() => {
    let sortableVideos = [...filteredVideos];
    if (sortConfig.key) {
      sortableVideos.sort((a, b) => {
        let aVal, bVal;
        if (sortConfig.key === "status") {
          aVal = a.lock ? "Locked" : "Unlocked";
          bVal = b.lock ? "Locked" : "Unlocked";
        } else {
          aVal = a[sortConfig.key]?.toString().toLowerCase() || "";
          bVal = b[sortConfig.key]?.toString().toLowerCase() || "";
        }

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sortableVideos;
  }, [filteredVideos, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle direction
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const totalEntries = sortedVideos.length;
  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const paginatedVideos = useMemo(() => {
    const start = (currentPage - 1) * entriesPerPage;
    return sortedVideos.slice(start, start + entriesPerPage);
  }, [sortedVideos, currentPage, entriesPerPage]);

  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  return (
    <div className="p-6 bg-[#211436] min-h-screen font-sans text-white">
      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg
              className="animate-spin h-6 w-6 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              ></path>
            </svg>
            <span className="text-gray-800 font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="mb-4 flex flex-wrap justify-between items-center gap-3 bg-[#351F57] rounded-[6px] px-4 py-3">
        <select
          value={selectedCategory}
          onChange={(e) => {
            setSelectedCategory(e.target.value);
            setCurrentPage(1);
          }}
          className="border-2 border-[#FFFFFFB2] bg-[#351F57] text-white rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#351F57] w-full sm:w-64"
        >
          {categoryOptions.map((cat, i) => (
            <option key={i} value={cat} className="bg-[#351F57] text-white">
              {cat}
            </option>
          ))}
        </select>

        <button
          type="button"
           onClick={() => navigate("/dashboard/addvideo")}
          className="bg-white text-[#5832AD] px-4 py-2 rounded hover:bg-[#111111] transition w-full sm:w-auto"
        >
          Add New Content +
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto p-3 bg-[#351F57] rounded-[6px] border border-transparent text-black">
        <table className="w-full text-left  text-sm text-white table-fixed border border-gray-200">
          <thead className="bg-[#482772]border-b border-gray-300">
            <tr className="divide-x divide-gray-300">
              <th className="p-4 cursor-pointer" onClick={() => requestSort("title")}>
                Title {getSortIcon("title")}
              </th>
              <th className="p-4 cursor-pointer" onClick={() => requestSort("contentType")}>
                Type {getSortIcon("contentType")}
              </th>
              <th className="p-4 cursor-pointer" onClick={() => requestSort("category")}>
                Category {getSortIcon("category")}
              </th>
              <th className="p-4 text-center cursor-pointer" onClick={() => requestSort("status")}>
                Status {getSortIcon("status")}
              </th>
              <th className="p-4 text-center">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {paginatedVideos.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-500">
                  No content found.
                </td>
              </tr>
            ) : (
              paginatedVideos.map((video, index) => (
                <tr
                  key={video.id}
                  className={`${
                    index % 2 === 0 ? "bg-[#482772]" : "bg-[#351F57]"
                  } divide-x divide-gray-300`}
                >

                  <td className="p-4 font-semibold text-white flex items-center space-x-1">
        <div className="w-[50px] h-[50px] flex-shrink-0">
          {(video.contentType || "video") === "image_text" ? (
            <img
              className="w-[50px] h-[50px] object-cover rounded-[8px] bg-gray-200"
              src={config.BASE_URL + "/uploads/" + video.url}
              alt={video.title}
            />
          ) : (video.contentType || "video") === "audio_text" ? (
            <div className="w-[50px] h-[50px] rounded-[8px] bg-[#5832AD] flex items-center justify-center text-white text-xs font-semibold">
              AUD
            </div>
          ) : (
            <video
              className="w-[50px] h-[50px] object-cover rounded-[8px] bg-gray-200"
              src={config.BASE_URL + "/uploads/" + video.url}
              preload="metadata"
              muted
              onLoadedMetadata={(e) => {
                e.target.currentTime = 1;
              }}
            />
          )}
        </div>
        <span className="truncate max-w-[200px]">{video.title}</span>
      </td>
                  <td className="p-4">{getContentTypeLabel(video.contentType || "video")}</td>
                  {/* <td className="p-4 font-semibold text-gray-900">
                    {video.title}
                  </td> */}
                  <td className="p-4">{video.category}</td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={video.lock}
                          onChange={() => handleToggleLock(video)}
                          className="sr-only peer"
                        />

                        {/* peer-focus:ring-2 peer-focus:ring-green */}
                        <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-700"></div>
                      </label>
                      <span
                        className={`text-sm font-medium ${
                          video.lock ? "text-red-600" : "text-green-600"
                        }`}
                      >
                        {video.lock ? "Locked" : "Unlocked"}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/edit-video/${video.id}`)}
                        className="px-3 py-1 text-sm bg-[#5832AD] text-white rounded-md hover:bg-purple-700"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteDialog({ open: true, id: video.id })
                        }
                        className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="mt-4 flex flex-col md:flex-row items-center justify-between text-white text-sm space-y-2 md:space-y-0">
          <div className="font-bold">
            Showing{" "}
            {totalEntries === 0
              ? 0
              : (currentPage - 1) * entriesPerPage + 1}{" "}
            to {Math.min(currentPage * entriesPerPage, totalEntries)} of{" "}
            {totalEntries} entries
          </div>

          <div className="flex items-center space-x-1">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-1 rounded-full hover:bg-[#351F57] hover:text-white disabled:opacity-50"
            >
              ‹
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`px-3 py-1 rounded ${
                  page === currentPage
                    ? "bg-[#351F57] text-white"
                    : "hover:bg-gray-200"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className="p-1 rounded-full hover:bg-[#351F57] hover:text-white disabled:opacity-50"
            >
              ›
            </button>
          </div>
        </div>
      </div>

      {/* Delete Dialog */}
      {deleteDialog.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-bold text-gray-800">Delete Content</h2>
            <p className="text-gray-600 mt-2">
              Are you sure you want to delete this content?
            </p>
            <div className="mt-4 flex justify-end space-x-3">
              <button
                onClick={() => setDeleteDialog({ open: false, id: null })}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryList;
